import { addMinutes } from 'date-fns';
import { Options, RRule as RRuleBase } from 'rrule';

const isUsableDate = (value: unknown): value is Date =>
  value instanceof Date && !isNaN(value.getTime());

export class RRule extends RRuleBase {
  static localToNaiveUTC(date: Date) {
    if (!date) {
      return date;
    } // Handle cases where no dtstart is provided
    const timezoneOffset = date.getTimezoneOffset();
    return addMinutes(date, -timezoneOffset);
  }

  static naiveUTCToLocal(date: Date) {
    if (!date) {
      return date;
    }
    const timezoneOffset = date.getTimezoneOffset();
    return addMinutes(date, timezoneOffset);
  }

  // set the dtstart to an actual occurrence of the rrule, to prevent discrepancies between the `between` and `all` methods
  private static alignAndConvertOptions(
    options: Partial<Options>,
  ): Partial<Options> {
    // Strip a missing or unusable dtstart so the underlying rrule library
    // doesn't reject it ("Invalid options: dtstart"). Defense-in-depth for
    // forward-compat: the task converter already coerces corrupt schedule
    // dates, but stale cached data or a future schema drift could still
    // surface a non-Date value here. Treating "missing" and "unusable" the
    // same lets the rule fall back to rrule's default rather than crash.
    if (!isUsableDate(options.dtstart)) {
      const { dtstart: _dtstart, ...rest } = options;
      return rest;
    }

    const intendedDtstart = RRule.localToNaiveUTC(options.dtstart);
    // create a temp rrule with the dtstart
    const tempRule = new RRuleBase({
      ...options,
      dtstart: intendedDtstart,
    });

    // get the first occurrence to actually set as dtstart
    const firstOccurrence = tempRule.after(intendedDtstart, true);

    return {
      ...options,
      dtstart: firstOccurrence || intendedDtstart,
    };
  }

  constructor(options: Partial<Options>, noCache?: boolean) {
    const alignedOptions = RRule.alignAndConvertOptions(options);
    super(alignedOptions as Options, noCache);
  }

  // Parsed rrule instances are cached by their source string so repeated
  // callers (e.g. schedule expansion across many sections) don't re-parse
  // and re-run alignAndConvertOptions on every call. Callers only read
  // `origOptions` from the returned instance and do not mutate it.
  private static fromStringCache = new Map<string, RRule>();

  static fromString(rruleString: string) {
    const cached = RRule.fromStringCache.get(rruleString);
    if (cached) {
      return cached;
    }
    const baseRrule = RRuleBase.fromString(rruleString);
    // remove wkst from the options, as this is not supported in our implementation currently
    const { wkst: _wkst, ...baseOptions } = baseRrule.origOptions;
    const instance = new RRule(baseOptions);
    RRule.fromStringCache.set(rruleString, instance);
    return instance;
  }

  private preProcess() {
    this.options.until = this.options.until
      ? RRule.localToNaiveUTC(this.options.until)
      : null;
  }
  private postProcess() {
    this.options.until = this.options.until
      ? RRule.naiveUTCToLocal(this.options.until)
      : null;
  }

  // Defensive: callers occasionally pass an Invalid Date when upstream
  // schedule data is corrupt (NaN max(), missing entry dates, etc.). The
  // underlying rrule library throws "Invalid date passed in to RRule.after"
  // on such inputs. Every consumer of `.after()` already handles `null`,
  // so returning `null` here is a safe degradation that avoids cascading
  // render crashes. Same rationale for `.before()` and `.between()` below.
  after(dt: Date, inc?: boolean): Date | null {
    if (!isUsableDate(dt)) {
      return null;
    }
    this.preProcess();
    const naiveUtcDate = RRule.localToNaiveUTC(dt);
    const result = super.after(naiveUtcDate, inc);
    this.postProcess();
    return result
      ? // Convert back to local time
        addMinutes(result, dt.getTimezoneOffset())
      : result;
  }

  between(
    after: Date,
    before: Date,
    inc?: boolean,
    iterator?: (d: Date, len: number) => boolean,
  ): Date[] {
    if (!isUsableDate(after) || !isUsableDate(before)) {
      return [];
    }
    this.preProcess();
    const naiveUtcStart = RRule.localToNaiveUTC(after);
    const naiveUtcEnd = RRule.localToNaiveUTC(before);
    const results = super.between(naiveUtcStart, naiveUtcEnd, inc, iterator);
    this.postProcess();
    return results.map((result) =>
      addMinutes(result, result.getTimezoneOffset()),
    );
  }

  before(dt: Date, inc?: boolean): Date | null {
    if (!isUsableDate(dt)) {
      return null;
    }
    this.preProcess();
    const naiveUtcDate = RRule.localToNaiveUTC(dt);
    const result = super.before(naiveUtcDate, inc);
    this.postProcess();
    return result
      ? // Convert back to local time
        addMinutes(result, result.getTimezoneOffset())
      : result;
  }

  all(iterator?: (d: Date, len: number) => boolean): Date[] {
    this.preProcess();
    const results = super.all(iterator);
    this.postProcess();

    return results.map((result) =>
      addMinutes(result, result.getTimezoneOffset()),
    );
  }

  toString() {
    // remove all unsuported options from the string
    return new RRuleBase({
      ...this.origOptions,
      dtstart: undefined,
      until: undefined,
      byhour: undefined,
      byminute: undefined,
      bysecond: undefined,
      wkst: undefined,
      count: undefined,
    }).toString();
  }
}
